/**
 * @file shop-errors.ts
 * @description Отказы API раздела «Магазины» → человеческий текст и поле
 * формы (phase19.28 этап А; uiux-platform-shops §7). Реестр кодов, а не
 * каскад условий: новый код — новая строка.
 *
 * Тексты slug и адресов — из `@chaiberi/shared` (те же, что у API), чтобы
 * подсказка при вводе и отказ сервера не расходились. Где сервер кладёт в
 * `message` название другого магазина («занят магазином «X»»), берём его.
 */

import {
  NORMALIZE_HOST_ERROR_MESSAGES,
  SHOP_SLUG_ERROR_MESSAGES,
  type NormalizeHostError,
} from "@chaiberi/shared";

import { ApiError } from "../api-client";
import { OWNER_INACTIVE_TEXT, ROLE_CONFLICT_TEXT } from "./shop-create-form";
import { formatPlatformDateTime } from "./shop-format";

/** Поле формы создания магазина, к которому относится отказ. */
export type ShopCreateField = "slug" | "displayName" | "timeZone" | "owner" | "ownerName" | "contactEmail" | "form";

interface CodeMeta {
  field: ShopCreateField;
  /** Свой текст; нет — текст сервера. */
  text?: string;
  /** Текст сервера важнее своего (в нём название магазина). */
  preferServer?: boolean;
}

const CREATE_CODES: Readonly<Record<string, CodeMeta>> = {
  shop_slug_invalid: { field: "slug", text: SHOP_SLUG_ERROR_MESSAGES.slug_invalid },
  shop_slug_reserved: { field: "slug", text: "Адрес зарезервирован платформой (app, api, static, edge, www…)" },
  shop_slug_taken: { field: "slug", text: "Этот адрес уже занят другим магазином", preferServer: true },
  shop_time_zone_invalid: { field: "timeZone", text: "Такого часового пояса нет — выберите из списка" },
  shop_display_name_invalid: { field: "displayName", text: "Название записано неверно", preferServer: true },
  shop_contact_email_invalid: { field: "contactEmail", text: "Почта магазина записана неверно", preferServer: true },
  // 409 shop_domain_taken при создании — поддомен уже занят: это адрес slug.
  shop_domain_taken: { field: "slug", text: "Адрес на платформе уже занят — выберите другой", preferServer: true },
  role_conflict: { field: "owner", text: ROLE_CONFLICT_TEXT },
  ROLE_CONFLICT: { field: "owner", text: ROLE_CONFLICT_TEXT },
  owner_not_found: { field: "owner", text: "Пользователь не найден — укажите почту ещё раз" },
  owner_inactive: { field: "owner", text: OWNER_INACTIVE_TEXT },
  owner_invalid: { field: "owner", text: "Владелец указан неверно — проверьте почту и имя", preferServer: true },
  owner_email_exists: { field: "owner", text: "Пользователь с этой почтой уже есть — он будет назначен владельцем, отправьте форму ещё раз" },
};

/** Отказ создания/правки магазина → поле и текст. */
export function shopCreateError(error: unknown): { field: ShopCreateField; message: string } {
  if (!(error instanceof ApiError)) return { field: "form", message: "Не удалось создать магазин. Повторите попытку" };
  const meta = CREATE_CODES[error.code];
  if (!meta) return { field: "form", message: error.message || "Не удалось создать магазин" };
  const message = meta.preferServer && error.message ? error.message : (meta.text ?? error.message);
  return { field: meta.field, message };
}

/** Подкод отказа адреса — `details.reason` (коды shared `normalizeHost`). */
function hostSubcode(details: unknown): NormalizeHostError | null {
  const reason = (details as { reason?: unknown } | null | undefined)?.reason;
  return typeof reason === "string" && Object.hasOwn(NORMALIZE_HOST_ERROR_MESSAGES, reason) ? (reason as NormalizeHostError) : null;
}

/** Отказ добавления адреса → текст под полем. */
export function shopDomainError(error: unknown): string {
  if (!(error instanceof ApiError)) return "Не удалось добавить адрес. Повторите попытку";
  if (error.code === "shop_domain_invalid") {
    const sub = hostSubcode(error.details);
    return sub ? NORMALIZE_HOST_ERROR_MESSAGES[sub] : error.message || "Это не похоже на доменное имя";
  }
  if (error.code === "shop_domain_taken") {
    const name = (error.details as { shopDisplayName?: unknown } | null | undefined)?.shopDisplayName;
    return typeof name === "string" && name ? `Домен уже привязан к магазину «${name}»` : error.message || "Домен уже привязан к другому магазину";
  }
  return error.message || "Не удалось добавить адрес";
}

/** Отказы действий над адресом (422) → тексты макета §5. */
const DOMAIN_ACTION_CODES: Readonly<Record<string, string>> = {
  shop_domain_primary: "Сначала сделайте главным другой адрес",
  shop_domain_subdomain: "Адрес платформы удалить нельзя",
  shop_domain_not_active: "Адрес отключён — сначала включите",
  shop_domain_primary_conflict: "Основной адрес только что сменили — обновите список",
};

/** Отказ «Сделать главным / Отключить / Удалить» → текст toast. */
export function domainActionError(error: unknown, fallback: string): string {
  if (error instanceof ApiError && DOMAIN_ACTION_CODES[error.code]) return DOMAIN_ACTION_CODES[error.code] ?? fallback;
  return apiErrorText(error, fallback);
}

/**
 * Отказ повторной проверки: 429 — «повторить можно через N с» (`details.retryAfterSec`),
 * иной 4xx — текст сервера, сеть и 5xx — «сервис не ответил», прошлый результат не стёрт.
 */
export function recheckErrorText(error: unknown, previousAt: string | null): string {
  if (error instanceof ApiError && error.status === 429) {
    const sec = (error.details as { retryAfterSec?: unknown } | null | undefined)?.retryAfterSec;
    return typeof sec === "number" && sec > 0 ? `Повторить можно через ${Math.ceil(sec)} с` : "Проверять адрес можно не чаще раза в минуту";
  }
  if (error instanceof ApiError && error.status >= 400 && error.status < 500 && error.message) return error.message;
  const when = previousAt ? ` Ниже — результат от ${formatPlatformDateTime(previousAt)}` : "";
  return `Не удалось проверить: сервис проверки не ответил.${when}`;
}

/** Текст любой ошибки для toast. */
export function apiErrorText(error: unknown, fallback: string): string {
  return error instanceof ApiError && error.message ? error.message : fallback;
}
